import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { colors, fontSizes, button, spacing, caption, borderRadius, overline } from 'ui/theme';
import { BADGE_VARIANTS } from './constants';
import { BADGE_VARIANT_TYPES } from './types';

type BaseBadgeProps = {
  variant: BADGE_VARIANT_TYPES;
};

export const BADGE_BASE_STYLE: FlattenSimpleInterpolation = css({
  ...button,
  borderRadius: borderRadius.md,
  lineHeight: fontSizes.s16,
  padding: `${spacing.quarter} ${spacing.half}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.quarter,
  outlineStyle: 'solid',
  outlineWidth: `${spacing.single}`,
  outlineOffset: `-${spacing.single}`,
});

export const BADGE_VARIANT_STYLES: Record<BADGE_VARIANT_TYPES, FlattenSimpleInterpolation> = {
  [BADGE_VARIANTS.DRAFT]: css`
    color: ${colors.textSecondary};
    background-color: ${colors.backgroundSecondary};
    outline-color: ${colors.borderStrong};
  `,
  [BADGE_VARIANTS.READY]: css`
    color: ${colors.textInfo};
    background-color: ${colors.backgroundInfoSubtle};
    outline-color: ${colors.borderInfoSubtle};
  `,
  [BADGE_VARIANTS.SCHEDULED]: css`
    color: ${colors.textWarning};
    background-color: ${colors.mustard10};
    outline-color: ${colors.mustard20};
  `,
  [BADGE_VARIANTS.RECURRING]: css`
    color: ${colors.textLink};
    background-color: ${colors.backgroundInteractive};
    outline-color: ${colors.borderInteractiveSubtle};
  `,
  [BADGE_VARIANTS.RUNNING]: css`
    color: ${colors.textSuccess};
    background-color: ${colors.backgroundSuccessSubtle};
    outline-color: ${colors.borderSuccessSubtle};
  `,
  [BADGE_VARIANTS.FINISHED]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundEmphasis};
    outline-color: ${colors.borderPrimary};
  `,
  [BADGE_VARIANTS.ABORTED]: css`
    color: ${colors.textDestructive};
    background-color: ${colors.backgroundDestructiveSubtle};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.INVERTED]: css`
    color: ${colors.iconOnColor};
    background-color: transparent;
    outline-color: ${colors.borderOnColor};
    mix-blend-mode: plus-lighter;
    opacity: 0.6;
  `,
  [BADGE_VARIANTS.RECALL]: css`
    color: ${colors.textDestructive};
    background-color: ${colors.backgroundDestructiveSubtle};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.BOSS]: css`
    background-color: ${colors.backgroundDestructiveSubtle};
    color: ${colors.textDestructive};
    ${overline};
    font-weight: 700;
    border-radius: ${borderRadius.sm};
    padding: ${spacing.eighth} ${spacing.quarter};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.POSITIVE]: css`
    color: ${colors.textOnColor};
    background-color: ${colors.backgroundSuccess};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.WARNING]: css`
    color: ${colors.textOnColor};
    background-color: ${colors.backgroundWarning};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.NEGATIVE]: css`
    color: ${colors.textOnColor};
    background-color: ${colors.backgroundDestructiveStrong};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.INFO]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundPrimary};
    box-shadow: 0px 0px 0px 1px ${colors.borderPrimary} inset;
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.NEUTRAL]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundTertiary};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.BETA]: css`
    color: ${colors.textSecondary};
    background-color: ${colors.backgroundTertiary};
    ${overline};
    font-weight: 700;
    border-radius: ${borderRadius.sm};
    padding: ${spacing.eighth} ${spacing.quarter};
    outline-color: ${colors.borderPrimary};
  `,
  [BADGE_VARIANTS.COUNTER]: css`
    ${caption}
    background-color: ${colors.backgroundInteractiveStrong};
    border-radius: ${borderRadius.full};
    box-sizing: border-box;
    color: ${colors.textOnColor};
    height: ${spacing.base};
    min-width: ${spacing.base};
    padding: 0 ${spacing.quarter};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.COUNTER_SUBTLE]: css`
    ${caption}
    background-color: ${colors.backgroundEmphasis};
    border-radius: ${borderRadius.full};
    box-sizing: border-box;
    color: ${colors.textSecondary};
    height: ${spacing.base};
    min-width: ${spacing.base};
    padding: 0 ${spacing.quarter};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.COUNTER_ERROR]: css`
    ${caption}
    background-color: ${colors.backgroundDestructiveStrong};
    border-radius: ${borderRadius.full};
    box-sizing: border-box;
    color: ${colors.textOnColor};
    height: ${spacing.base};
    min-width: ${spacing.base};
    padding: 0 ${spacing.quarter};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.INACTIVE]: css`
    color: ${colors.textDisabled};
    background-color: ${colors.backgroundDisabled};
    outline-color: ${colors.borderPrimary};
  `,
  [BADGE_VARIANTS.ACTIVE]: css`
    color: ${colors.textInfo};
    background-color: ${colors.backgroundInfoSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderInfoSubtle};
  `,
  [BADGE_VARIANTS.ERRORED]: css`
    color: ${colors.textDestructive};
    background-color: ${colors.backgroundDestructiveSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.CANCELLED]: css`
    color: ${colors.textDestructive};
    background-color: ${colors.backgroundDestructiveSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.WINNER_FOUND]: css`
    color: ${colors.textSuccess};
    background-color: ${colors.backgroundSuccessSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderSuccessSubtle};
  `,
  [BADGE_VARIANTS.USER_SELECTED_WINNER]: css`
    color: ${colors.textSuccess};
    background-color: ${colors.backgroundSuccessSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderSuccessSubtle};
  `,
  [BADGE_VARIANTS.SENDING_TO_REMAINDER]: css`
    color: ${colors.textWarning};
    background-color: ${colors.mustard10};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.mustard20};
  `,
  [BADGE_VARIANTS.AI]: css`
    box-shadow: 0px 0px 0px 1px ${colors.borderPrimary} inset;
    background-color: ${colors.backgroundPrimary};
    color: ${colors.textLink};
    ${overline};
    gap: ${spacing.eighth};
    line-height: ${spacing.threeQuarter};
    padding-left: ${spacing.quarter};
    outline-color: transparent;
  `,
  [BADGE_VARIANTS.IN_APPEAL]: css`
    color: ${colors.textWarning};
    background-color: ${colors.mustard10};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.mustard20};
  `,
  [BADGE_VARIANTS.UNKNOWN]: css`
    color: ${colors.textSecondary};
    background-color: ${colors.backgroundTertiary};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderPrimary};
  `,
  [BADGE_VARIANTS.PENDING]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundSecondary};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderStrong};
  `,
  [BADGE_VARIANTS.ARCHIVED]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundSecondary};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderStrong};
  `,
  [BADGE_VARIANTS.ENABLED]: css`
    color: ${colors.textSuccess};
    background-color: ${colors.backgroundSuccessSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderSuccessSubtle};
  `,
  [BADGE_VARIANTS.NEEDS_REVIEW]: css`
    color: ${colors.textDestructive};
    background-color: ${colors.backgroundDestructiveSubtle};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderDestructiveSubtle};
  `,
  [BADGE_VARIANTS.DISABLED]: css`
    color: ${colors.textPrimary};
    background-color: ${colors.backgroundSecondary};
    padding: ${spacing.quarter} ${spacing.half};
    outline-color: ${colors.borderStrong};
  `,
};

export const BaseBadge = styled.span<BaseBadgeProps>`
  ${() => BADGE_BASE_STYLE}
  ${({ variant }) => BADGE_VARIANT_STYLES[variant] || ''};
`;
